// src/PermissionsManager.jsx - Admin panel for role/page permissions + user management
// - Section 1: Role/Page permission matrix (keeps your existing functionality)
// - Section 2: Users table (manage role + active flag via /api/users endpoints from 2c)
// Requirements:
//   - Server routes (Admin-only):
//       GET    /api/users
//       PATCH  /api/users/:id/role      { role: "New User" | "Customer" | "Data Collector" | "Hendler" | "Admin" }
//       PATCH  /api/users/:id/active    { isActive: boolean }
//   - Client has axios defaults (baseURL + JWT header) set globally (via ./api)

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './api'; // ensures axios baseURL + token header are set globally

const buildStyles = (isDark) => {
  const palette = isDark ? {
    bg: '#050b14',
    card: '#0b1321',
    border: '#1f2a3a',
    text: '#e5edf5',
    muted: '#9fb2c5',
    accent: '#3b82f6',
    accentStrong: '#1d4ed8',
    tableHead: '#0e1a2c',
    pillBg: 'rgba(255,255,255,0.08)',
    pillBorder: 'rgba(255,255,255,0.12)',
    inputBg: '#0b1524',
    inputBorder: '#243447',
    shadow: '0 10px 30px rgba(0,0,0,0.25)'
  } : {
    bg: '#f7fafc',
    card: '#ffffff',
    border: '#d7e2ee',
    text: '#0f172a',
    muted: '#475569',
    accent: '#2563eb',
    accentStrong: '#1d4ed8',
    tableHead: '#f1f5f9',
    pillBg: 'rgba(0,0,0,0.04)',
    pillBorder: 'rgba(0,0,0,0.07)',
    inputBg: '#ffffff',
    inputBorder: '#cbd5e1',
    shadow: '0 10px 30px rgba(15,23,42,0.08)'
  };

  return {
    page: {
      padding: 20,
      background: palette.bg,
      color: palette.text,
      minHeight: '100vh'
    },
    shell: {
      width: '100%',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: 18
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap'
    },
    heading: { margin: 0, fontSize: 24, color: palette.text },
    subheading: { margin: '4px 0 0', color: palette.muted, fontSize: 14 },
    statPill: {
      display: 'inline-flex',
      padding: '6px 10px',
      borderRadius: 12,
      background: palette.pillBg,
      color: isDark ? '#0ea5e9' : '#0f172a',
      border: `1px solid ${palette.pillBorder}`,
      fontWeight: 600,
      fontSize: 12
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: 14
    },
    card: {
      background: palette.card,
      borderRadius: 16,
      border: `1px solid ${palette.border}`,
      padding: 16,
      boxShadow: palette.shadow
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 12,
      gap: 10,
      color: palette.text
    },
    controlsRow: {
      display: 'flex',
      gap: 10,
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    input: {
      borderRadius: 10,
      border: `1px solid ${palette.inputBorder}`,
      background: palette.inputBg,
      color: palette.text,
      padding: '10px 12px',
      minWidth: 220
    },
    button: {
      borderRadius: 10,
      border: `1px solid ${palette.accent}`,
      background: `linear-gradient(135deg, ${palette.accent}, ${palette.accentStrong})`,
      color: 'white',
      padding: '10px 14px',
      cursor: 'pointer',
      fontWeight: 600
    },
    tableWrap: { overflow: 'hidden', borderRadius: 12, border: `1px solid ${palette.border}`, width: '100%' },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
      minWidth: 1200,
      background: palette.card
    },
    tableHead: { background: palette.tableHead },
    pill: {
      display: 'inline-flex',
      padding: '6px 10px',
      borderRadius: 999,
      background: palette.pillBg,
      border: `1px solid ${palette.pillBorder}`,
      fontSize: 12,
      color: palette.text
    },
    palette
  };
};

export default function PermissionsManager({ isDark = false }) {
  const styles = useMemo(() => buildStyles(isDark), [isDark]);
  // -----------------------------
  // SECTION 1: ROLE/PAGE PERMISSIONS
  // -----------------------------
  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const levels = ['none', 'read', 'write'];
  const [permLoading, setPermLoading] = useState(true);
  const [permError, setPermError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      setPermLoading(true);
      setPermError('');
      try {
        const { data } = await axios.get('/api/permissions');
        if (!mounted) return;
        setRoles(data.roles || []);
        setPages(data.pages || []);
        setPermissions(data.permissions || []);
      } catch (err) {
        console.error('Failed to fetch permissions data:', err);
        if (mounted) setPermError(err?.response?.data?.error || 'Failed to load permissions');
      } finally {
        if (mounted) setPermLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const getLevel = (role, pageName) => {
    const perm = permissions.find(p => p.Role === role && p.PageName === pageName);
    return perm ? perm.AccessLevel : 'none';
  };

  const updateLevel = async (role, pageName, newLevel) => {
    try {
      await axios.patch('/api/permissions', { role, pageName, accessLevel: newLevel });
      setPermissions(prev =>
        prev.map(p =>
          p.Role === role && p.PageName === pageName ? { ...p, AccessLevel: newLevel } : p
        )
      );
    } catch (err) {
      console.error('Failed to update level:', err);
      alert(`Failed to update permission: ${err?.response?.data?.error || 'Server error'}`);
    }
  };

  // -----------------------------
  // SECTION 2: USER MANAGEMENT (roles + active)
  // -----------------------------
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [filter, setFilter] = useState('');
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  // -----------------------------
  // SECTION 3: SCRAPING MONITOR
  // -----------------------------
  const [scrapingData, setScrapingData] = useState(null);
  const [scrapingLoading, setScrapingLoading] = useState(false);
  const [scrapingError, setScrapingError] = useState('');
  const [clearingUserKey, setClearingUserKey] = useState(null);

  const fetchScrapingData = async () => {
    setScrapingLoading(true);
    setScrapingError('');
    try {
      const { data } = await axios.get('/api/scraping-monitor');
      setScrapingData(data);
    } catch (err) {
      console.error('Failed to fetch scraping data:', err);
      setScrapingError(err?.response?.data?.error || 'Failed to load scraping data');
    } finally {
      setScrapingLoading(false);
    }
  };

  const clearUserFlags = async (userKey) => {
    setClearingUserKey(userKey);
    try {
      await axios.delete(`/api/scraping-monitor/${userKey}`);
      // Refresh the data
      await fetchScrapingData();
    } catch (err) {
      console.error('Failed to clear user flags:', err);
      setScrapingError(err?.response?.data?.error || 'Failed to clear flags');
    } finally {
      setClearingUserKey(null);
    }
  };

  useEffect(() => {
    fetchScrapingData();
  }, []);

  // Fetch current user profile from server instead of trusting localStorage
  // This prevents users from spoofing their role by editing localStorage
  const me = useMemo(() => {
    if (currentUserProfile) {
      return {
        username: (currentUserProfile.Username || '').toLowerCase(),
        role: currentUserProfile.Role || ''
      };
    }
    // Fallback to username from localStorage (safe - just for display)
    // but role is empty until we fetch from server
    return {
      username: (localStorage.getItem('username') || '').toLowerCase(),
      role: ''
    };
  }, [currentUserProfile]);

  // Fetch current user's profile from server on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await axios.get('/api/users/me');
        setCurrentUserProfile(data);
      } catch (err) {
        console.error('Failed to fetch current user profile:', err);
      }
    };
    fetchCurrentUser();
  }, []);

  const fetchUsers = async () => {
    setUserLoading(true);
    setUserError('');
    try {
      const { data } = await axios.get('/api/users');
      const normalized = (data || []).map(u => ({
        ...u,
        Username: (u.Username || '').toLowerCase(),
        _role: u.Role,
        _isActive: !!u.IsActive,
        AccountNumber: (u.AccountNumber || '').toUpperCase(),
        _accountNumber: (u.AccountNumber || '').toUpperCase(),
        OrderwiseUserId: Number.isFinite(Number(u.OrderwiseUserId)) ? Number(u.OrderwiseUserId) : null,
        _orderwiseUserId: (u.OrderwiseUserId === null || u.OrderwiseUserId === undefined) ? '' : String(u.OrderwiseUserId),
        _dirty: false
      })).sort((a, b) => a.Username.localeCompare(b.Username));
      setUsers(normalized);
    } catch (e) {
      console.error(e);
      setUserError(e?.response?.data?.error || 'Failed to load users');
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const filteredUsers = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return users;
    return users.filter(u =>
      u.Username.includes(f) ||
      (u.DisplayName || '').toLowerCase().includes(f) ||
      (u.Email || '').toLowerCase().includes(f) ||
      (u.Role || '').toLowerCase().includes(f) ||
      (u.AccountNumber || '').toLowerCase().includes(f) ||
      String(u.OrderwiseUserId ?? '').toLowerCase().includes(f)
    );
  }, [users, filter]);

  const markDirty = (id, updater) => {
    setUsers(prev => prev.map(u => {
      if (u.UserKey !== id) return u;
      const next = updater({ ...u });
      next._dirty =
        (next._role !== u.Role) ||
        (next._isActive !== !!u.IsActive) ||
        ((next._accountNumber || '') !== (u.AccountNumber || '')) ||
        (String(next._orderwiseUserId ?? '') !== String(u.OrderwiseUserId ?? ''));
      return next;
    }));
  };

  const saveUser = async (u) => {
    setSavingId(u.UserKey);
    setUserError('');
    try {
      if (u._role !== u.Role) {
        await axios.patch(`/api/users/${u.UserKey}/role`, { role: u._role });
      }
      if (u._isActive !== !!u.IsActive) {
        await axios.patch(`/api/users/${u.UserKey}/active`, { isActive: u._isActive });
      }
      if ((u._accountNumber || '') !== (u.AccountNumber || '')) {
        await axios.patch(`/api/users/${u.UserKey}/account-number`, { accountNumber: u._accountNumber });
      }

      const prevOrderwise = (u.OrderwiseUserId === null || u.OrderwiseUserId === undefined) ? '' : String(u.OrderwiseUserId);
      const nextOrderwise = (u._orderwiseUserId || '').trim();
      if (nextOrderwise !== prevOrderwise) {
        await axios.patch(`/api/users/${u.UserKey}/orderwise-user-id`, {
          orderwiseUserId: nextOrderwise ? parseInt(nextOrderwise, 10) : null
        });
      }

      setUsers(prev => prev.map(x => x.UserKey === u.UserKey
        ? {
          ...x,
          Role: u._role,
          IsActive: u._isActive,
          AccountNumber: u._accountNumber || '',
          OrderwiseUserId: nextOrderwise ? parseInt(nextOrderwise, 10) : null,
          _dirty: false
        }
        : x
      ));
      // keep UI consistent if we edited ourselves
      if (u.Username === me.username) localStorage.setItem('role', u._role);
    } catch (e) {
      console.error(e);
      setUserError(e?.response?.data?.error || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const revertUser = (u) => {
    setUsers(prev => prev.map(x => x.UserKey === u.UserKey
      ? {
        ...x,
        _role: x.Role,
        _isActive: !!x.IsActive,
        _accountNumber: x.AccountNumber || '',
        _orderwiseUserId: (x.OrderwiseUserId === null || x.OrderwiseUserId === undefined) ? '' : String(x.OrderwiseUserId),
        _dirty: false
      }
      : x
    ));
  };

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.heading}>Permissions Manager</h2>
            <p style={styles.subheading}>Control page access, user roles, and who can see trade pricing.</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={styles.statPill}>Pages: {pages.length || 0}</span>
            <span style={styles.statPill}>Roles: {roles.length || 0}</span>
            <span style={styles.statPill}>Users: {users.length || 0}</span>
          </div>
        </div>

        <div style={styles.grid}>
          {/* Role/Page matrix */}
          <section style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={{ margin: 0 }}>Role & Page Access</h3>
              {permLoading && <span style={styles.pill}>Loading…</span>}
            </div>
            {permError && <Banner type="error">{permError}</Banner>}
            {permLoading ? (
              <div style={{ padding: 10 }}>Loading permissions…</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead style={styles.tableHead}>
                      <tr>
                        <Th palette={styles.palette}>Page</Th>
                        {roles.map(role => <Th key={role} palette={styles.palette}>{role}</Th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {pages.map(page => (
                        <tr key={page.PermissionID} style={{ borderBottom: `1px solid ${styles.palette.border}` }}>
                          <Td palette={styles.palette}>{page.PageName}</Td>
                          {roles.map(role => (
                            <Td key={role} palette={styles.palette}>
                              <select
                                value={getLevel(role, page.PageName)}
                                onChange={e => updateLevel(role, page.PageName, e.target.value)}
                                style={{ width: '100%' }}
                              >
                                {levels.map(l => <option key={l} value={l}>{l}</option>)}
                              </select>
                            </Td>
                          ))}
                        </tr>
                      ))}
                      {!pages.length && (
                        <tr>
                          <Td
                            palette={styles.palette}
                            colSpan={1 + roles.length}
                            center
                            style={{ color: styles.palette.muted }}
                          >
                            No pages configured
                          </Td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* Users & roles */}
          <section style={{ ...styles.card, gridColumn: '1 / -1' }}>
            <div style={styles.cardHeader}>
              <div>
                <h3 style={{ margin: 0 }}>Users & Roles</h3>
                <p style={{ margin: '4px 0 0', color: styles.palette.muted, fontSize: 13 }}>Search by username, display name, email, role, account number, or OrderWise user ID.</p>
              </div>
              <div style={styles.controlsRow}>
                <input
                  placeholder="Filter by username, name, email, role or account #"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  style={{ ...styles.input, minWidth: 260 }}
                />
                <button onClick={fetchUsers} disabled={userLoading} style={styles.button}>
                  {userLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
            </div>

            {userError && <Banner type="error">{userError}</Banner>}

            <div style={{ overflowX: 'auto' }}>
              <div style={styles.tableWrap}>
                <table style={{ ...styles.table, minWidth: 1400 }}>
                  <thead style={styles.tableHead}>
                    <tr>
                      <Th palette={styles.palette}>ID</Th>
                      <Th palette={styles.palette}>Username</Th>
                      <Th palette={styles.palette} style={{ width: '72ch', maxWidth: '72ch' }}>Display name</Th>
                      <Th palette={styles.palette}>Email</Th>
                      <Th palette={styles.palette} style={{ width: '16ch' }}>Account #</Th>
                      <Th palette={styles.palette} style={{ width: '16ch' }}>OrderWise user ID</Th>
                      <Th palette={styles.palette}>Role</Th>
                      <Th palette={styles.palette} center>Active</Th>
                      <Th palette={styles.palette} style={{ width: '26ch' }}>Last login</Th>
                      <Th palette={styles.palette}>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => {
                      const isMe = u.Username === me.username;
                      return (
                        <tr key={u.UserKey} style={{
                          borderBottom: `1px solid ${styles.palette.border}`,
                          background: isMe ? 'rgba(59,130,246,0.08)' : (u._dirty ? 'rgba(16,185,129,0.08)' : 'transparent')
                        }}>
                          <Td mono palette={styles.palette}>{u.UserKey}</Td>
                          <Td strong={isMe} palette={styles.palette}>
                            {u.Username}
                            {isMe && <span style={{ marginLeft: 6, fontSize: 12, color: '#38bdf8' }}>(you)</span>}
                          </Td>
                          <Td style={{ width: '72ch', maxWidth: '72ch' }} palette={styles.palette}>{u.DisplayName || '—'}</Td>
                          <Td palette={styles.palette}>{u.Email || '—'}</Td>
                          <Td palette={styles.palette}>
                            <input
                              value={u._accountNumber}
                              onChange={e => markDirty(u.UserKey, r => { r._accountNumber = e.target.value.toUpperCase(); return r; })}
                              disabled={savingId === u.UserKey}
                              style={{ width: '100%', ...styles.input, padding: '8px 10px' }}
                              placeholder="Optional"
                            />
                          </Td>
                          <Td palette={styles.palette}>
                            <input
                              type="number"
                              inputMode="numeric"
                              value={u._orderwiseUserId}
                              onChange={e => markDirty(u.UserKey, r => { r._orderwiseUserId = e.target.value; return r; })}
                              disabled={savingId === u.UserKey}
                              style={{ width: '100%', ...styles.input, padding: '8px 10px' }}
                              placeholder="Optional"
                              min={1}
                              step={1}
                            />
                          </Td>
                          <Td palette={styles.palette}>
                            <select
                              value={u._role}
                              onChange={e => markDirty(u.UserKey, r => { r._role = e.target.value; return r; })}
                              disabled={savingId === u.UserKey}
                              style={{ ...styles.input, padding: '8px 10px' }}
                            >
                              {['Admin', 'Data Collector','Warehouse', 'Hendler', 'Customer', 'New User'].map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          </Td>
                          <Td center palette={styles.palette}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="checkbox"
                                checked={u._isActive}
                                onChange={e => markDirty(u.UserKey, r => { r._isActive = e.target.checked; return r; })}
                                disabled={savingId === u.UserKey}
                              />
                              <span>{u._isActive ? 'Enabled' : 'Disabled'}</span>
                            </label>
                          </Td>
                          <Td mono palette={styles.palette} style={{ width: '26ch', whiteSpace: 'nowrap' }}>{u.LastLoginAt ? new Date(u.LastLoginAt).toLocaleString() : '—'}</Td>
                          <Td palette={styles.palette}>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                onClick={() => saveUser(u)}
                                disabled={!u._dirty || savingId === u.UserKey}
                                style={{ ...styles.button, padding: '8px 12px' }}
                              >
                                {savingId === u.UserKey ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                onClick={() => revertUser(u)}
                                disabled={!u._dirty || savingId === u.UserKey}
                                style={{ ...styles.button, background: 'transparent', color: '#dbe7f3', borderColor: '#64748b' }}
                              >
                                Revert
                              </button>
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                    {!userLoading && filteredUsers.length === 0 && (
                      <tr>
                        <Td
                          palette={styles.palette}
                          colSpan={10}
                          center
                          style={{ color: styles.palette.muted }}
                        >
                          No users
                        </Td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Security Monitor - Scraping Detection */}
          <section style={{ ...styles.card, gridColumn: '1 / -1' }}>
            <div style={styles.cardHeader}>
              <div>
                <h3 style={{ margin: 0 }}>🛡️ Security Monitor</h3>
                <p style={{ margin: '4px 0 0', color: styles.palette.muted, fontSize: 13 }}>
                  Monitor users flagged for suspicious activity (potential scraping).
                </p>
              </div>
              <div style={styles.controlsRow}>
                {scrapingData && (
                  <span style={{
                    ...styles.statPill,
                    background: scrapingData.enabled ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
                    color: scrapingData.enabled ? '#10b981' : '#ef4444'
                  }}>
                    {scrapingData.enabled ? '✓ Protection Enabled' : '✗ Protection Disabled'}
                  </span>
                )}
                <button onClick={fetchScrapingData} disabled={scrapingLoading} style={styles.button}>
                  {scrapingLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
            </div>

            {scrapingError && <Banner type="error">{scrapingError}</Banner>}

            {scrapingData && (
              <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ ...styles.pill, background: 'rgba(251,191,36,0.15)', borderColor: 'rgba(251,191,36,0.3)' }}>
                  ⚠️ Warning: {scrapingData.thresholds?.warn || 100} req/hr
                </div>
                <div style={{ ...styles.pill, background: 'rgba(249,115,22,0.15)', borderColor: 'rgba(249,115,22,0.3)' }}>
                  🐢 Throttle: {scrapingData.thresholds?.slow || 200} req/hr
                </div>
                <div style={{ ...styles.pill, background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.3)' }}>
                  🚫 Block: {scrapingData.thresholds?.block || 500} req/hr
                </div>
                <div style={{ ...styles.pill }}>
                  Window: {Math.round((scrapingData.windowMs || 3600000) / 60000)} min
                </div>
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <div style={styles.tableWrap}>
                <table style={{ ...styles.table, minWidth: 900 }}>
                  <thead style={styles.tableHead}>
                    <tr>
                      <Th palette={styles.palette}>User ID</Th>
                      <Th palette={styles.palette}>Username</Th>
                      <Th palette={styles.palette} center>Requests</Th>
                      <Th palette={styles.palette} center>Level</Th>
                      <Th palette={styles.palette}>Flagged At</Th>
                      <Th palette={styles.palette}>Endpoint Breakdown</Th>
                      <Th palette={styles.palette}>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(scrapingData?.flaggedUsers || []).map(f => {
                      const user = users.find(u => u.UserKey === f.userKey);
                      const levelColors = {
                        warn: { bg: 'rgba(251,191,36,0.2)', color: '#fbbf24', label: '⚠️ Warning' },
                        slow: { bg: 'rgba(249,115,22,0.2)', color: '#f97316', label: '🐢 Throttled' },
                        block: { bg: 'rgba(239,68,68,0.2)', color: '#ef4444', label: '🚫 Blocked' }
                      };
                      const levelStyle = levelColors[f.level] || levelColors.warn;
                      
                      return (
                        <tr key={f.userKey} style={{ borderBottom: `1px solid ${styles.palette.border}` }}>
                          <Td mono palette={styles.palette}>{f.userKey}</Td>
                          <Td palette={styles.palette}>{user?.Username || user?.DisplayName || '—'}</Td>
                          <Td center palette={styles.palette}>
                            <span style={{ fontWeight: 700, fontSize: 16 }}>{f.requestsInWindow}</span>
                          </Td>
                          <Td center palette={styles.palette}>
                            <span style={{
                              ...styles.pill,
                              background: levelStyle.bg,
                              color: levelStyle.color,
                              fontWeight: 600
                            }}>
                              {levelStyle.label}
                            </span>
                          </Td>
                          <Td palette={styles.palette} style={{ whiteSpace: 'nowrap' }}>
                            {f.flaggedAt ? new Date(f.flaggedAt).toLocaleString() : '—'}
                          </Td>
                          <Td palette={styles.palette}>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {Object.entries(f.endpointBreakdown || {}).map(([endpoint, count]) => (
                                <span key={endpoint} style={{ ...styles.pill, fontSize: 11 }}>
                                  {endpoint}: {count}
                                </span>
                              ))}
                            </div>
                          </Td>
                          <Td palette={styles.palette}>
                            <button
                              onClick={() => clearUserFlags(f.userKey)}
                              disabled={clearingUserKey === f.userKey}
                              style={{
                                ...styles.button,
                                padding: '6px 10px',
                                fontSize: 12,
                                background: 'transparent',
                                color: styles.palette.accent,
                                borderColor: styles.palette.accent
                              }}
                            >
                              {clearingUserKey === f.userKey ? 'Clearing…' : 'Clear Flags'}
                            </button>
                          </Td>
                        </tr>
                      );
                    })}
                    {!scrapingLoading && (!scrapingData?.flaggedUsers || scrapingData.flaggedUsers.length === 0) && (
                      <tr>
                        <Td
                          palette={styles.palette}
                          colSpan={7}
                          center
                          style={{ color: styles.palette.muted, padding: 24 }}
                        >
                          ✓ No users currently flagged for suspicious activity
                        </Td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {scrapingData?.flaggedUsers?.length > 0 && (
              <p style={{ marginTop: 12, fontSize: 12, color: styles.palette.muted }}>
                💡 Flagged users receive progressive delays on sensitive endpoints (product search, fitments, pricing).
                Use "Clear Flags" to reset a user's counter if they were flagged incorrectly.
              </p>
            )}
          </section>

          {/* ── OrderWise Search Mirror Sync ── */}
          <SearchMirrorSync styles={styles} />
        </div>
      </div>
    </div>
  );
}

// ---------- OrderWise Search Mirror Sync ----------
function SearchMirrorSync({ styles }) {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get('/api/search-mirror/status');
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch mirror status:', err);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    setResult(null);
    setError('');
    try {
      const { data } = await axios.post('/api/search-mirror/sync');
      if (data.success) {
        setResult(`Sync complete — ${data.count?.toLocaleString()} products synced in ${(data.elapsed / 1000).toFixed(1)}s`);
      } else {
        setError(data.reason || 'Sync failed');
      }
      await fetchStatus();
    } catch (err) {
      setError(err?.response?.data?.reason || err?.message || 'Sync request failed');
    } finally {
      setSyncing(false);
    }
  };

  const lastSync = status?.lastSyncAt
    ? new Date(status.lastSyncAt).toLocaleString()
    : 'Never';

  return (
    <section style={{ ...styles.card, gridColumn: '1 / -1' }}>
      <div style={styles.cardHeader}>
        <div>
          <h3 style={{ margin: 0 }}>OrderWise Search Mirror</h3>
          <p style={{ margin: '4px 0 0', color: styles.palette.muted, fontSize: 13 }}>
            Sync product search data from OrderWise to the local database for faster searching.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{
            ...styles.button,
            opacity: syncing ? 0.6 : 1,
            cursor: syncing ? 'not-allowed' : 'pointer'
          }}
        >
          {syncing ? 'Syncing…' : 'Sync Now'}
        </button>

        {status && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={styles.pill}>Last sync: {lastSync}</span>
            <span style={styles.pill}>Products: {status.lastSyncCount?.toLocaleString() || 0}</span>
            <span style={styles.pill}>Interval: {status.syncIntervalMs ? `${Math.round(status.syncIntervalMs / 60000)}m` : '—'}</span>
            {status.syncInProgress && <span style={{ ...styles.pill, background: 'rgba(59,130,246,0.15)', color: '#60a5fa' }}>Sync in progress</span>}
          </div>
        )}
      </div>

      {result && (
        <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: 'rgba(16,185,129,0.14)', color: '#bbf7d0', border: '1px solid rgba(16,185,129,0.35)' }}>
          ✓ {result}
        </div>
      )}
      {error && (
        <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: 'rgba(239,68,68,0.2)', color: '#fecdd3', border: '1px solid rgba(248,113,113,0.4)' }}>
          ✗ {error}
        </div>
      )}
      {status?.lastSyncError && !error && !result && (
        <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: 'rgba(239,68,68,0.12)', color: '#fca5a5', border: '1px solid rgba(248,113,113,0.3)', fontSize: 13 }}>
          Last sync error: {status.lastSyncError}
        </div>
      )}
    </section>
  );
}

// ---------- small presentational helpers ----------
function Banner({ children, type = 'info' }) {
  const colors = type === 'error'
    ? { bg: 'rgba(239,68,68,0.2)', fg: '#fecdd3', border: 'rgba(248,113,113,0.4)' }
    : { bg: 'rgba(16,185,129,0.14)', fg: '#bbf7d0', border: 'rgba(16,185,129,0.35)' };
  return (
    <div style={{
      marginBottom: 10, padding: 12, borderRadius: 10,
      background: colors.bg, color: colors.fg, border: `1px solid ${colors.border}`
    }}>
      {children}
    </div>
  );
}
function Th({ children, center, style, palette }) {
  return (
    <th style={{
      textAlign: center ? 'center' : 'left',
      padding: '12px 10px', fontWeight: 700, fontSize: 13,
      color: palette.text,
      borderBottom: `1px solid ${palette.border}`,
      letterSpacing: 0.2,
      ...style
    }}>
      {children}
    </th>
  );
}
function Td({ children, mono, strong, center, style, palette, ...rest }) {
  return (
    <td
      {...rest}
      style={{
        padding: '11px 10px',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' : 'inherit',
        fontWeight: strong ? 700 : 400,
        textAlign: center ? 'center' : 'left',
        verticalAlign: 'middle',
        color: palette.text,
        ...style
      }}
    >
      {children}
    </td>
  );
}
